import axios from 'axios'
import { message } from 'ant-design-vue'
import router from '@/router'

// 创建一个专门用于刷新令牌的axios实例（避免循环依赖）
const refreshTokenApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 刷新令牌的函数
const refreshAccessToken = async (refreshToken) => {
  try {
    const response = await refreshTokenApi.post('/users/token/refresh/', {
      refresh: refreshToken
    })
    return response.data.access
  } catch (error) {
    console.error('令牌刷新失败:', error)
    throw error
  }
}

// 创建axios实例
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}')
      if (tokens.access) {
        config.headers.Authorization = `Bearer ${tokens.access}`
      }
    } catch (e) {
      // 忽略解析错误
    }
    
    // 添加租户信息
    try {
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}')
      if (tenant.id) {
        config.headers['X-Tenant-ID'] = tenant.id
      }
    } catch (e) {
      // 忽略解析错误
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error) => {
    const originalRequest = error.config
    const { response } = error

    // 处理HTTP状态码
    if (response) {
      switch (response.status) {
        case 401:
          // 如果是401错误且不是刷新令牌请求本身
          if (!originalRequest._retry) {
            originalRequest._retry = true
            
            try {
              // 尝试刷新令牌
              const tokens = JSON.parse(localStorage.getItem('tokens') || '{}')
              if (tokens.refresh) {
                const newAccessToken = await refreshAccessToken(tokens.refresh)
                
                // 更新本地存储的令牌
                const updatedTokens = {
                  ...tokens,
                  access: newAccessToken
                }
                localStorage.setItem('tokens', JSON.stringify(updatedTokens))
                
                // 重试原始请求
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
                return api(originalRequest)
              }
            } catch (refreshError) {
              // 刷新令牌失败，清除本地存储并跳转到登录页
              console.error('令牌刷新失败，需要重新登录:', refreshError)
              localStorage.removeItem('user')
              localStorage.removeItem('tenant')
              localStorage.removeItem('tokens')
              localStorage.removeItem('currentStore')
              if (router.currentRoute.value.path !== '/login') {
                router.push('/login')
              }
              message.error('登录已过期，请重新登录')
            }
          } else {
            // 已经重试过仍然失败的情况
            localStorage.removeItem('user')
            localStorage.removeItem('tenant')
            localStorage.removeItem('tokens')
            localStorage.removeItem('currentStore')
            if (router.currentRoute.value.path !== '/login') {
              router.push('/login')
            }
            message.error('登录已过期，请重新登录')
          }
          break
          
        case 403:
          message.error('权限不足')
          break
          
        case 404:
          // 更友好的404错误提示，避免显示"请求的资源不存在"
          message.error('请求的接口不存在或服务器配置有误')
          break
          
        case 422:
          // 表单验证错误
          if (response.data && response.data.errors) {
            const firstError = Object.values(response.data.errors)[0]
            message.error(Array.isArray(firstError) ? firstError[0] : firstError)
          } else {
            message.error('请求参数错误')
          }
          break
          
        case 500:
          message.error('服务器内部错误')
          break
          
        default:
          // 安全地处理错误消息
          let errorMessage = '请求失败'
          if (response.data) {
            if (typeof response.data === 'string') {
              errorMessage = response.data
            } else if (response.data.message) {
              errorMessage = response.data.message
            } else if (response.data.error) {
              errorMessage = response.data.error
            } else if (response.data.detail) {
              errorMessage = response.data.detail
            }
          }
          message.error(errorMessage)
      }
    } else if (error.code === 'ECONNABORTED') {
      message.error('请求超时')
    } else if (error.message && error.message.includes('Network Error')) {
      message.error('网络连接错误，请检查后端服务是否正常运行')
    } else {
      message.error('网络错误')
    }
    
    return Promise.reject(error)
  }
)

export default api